import { useCallback, useMemo, useState } from 'react';
import type { SingleValue } from 'react-select';
import Select from 'react-select';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { Header } from '@trinity/chat-components/components/Modal/Header.js';
import { useModal } from '@trinity/chat-components/components/Modal/hooks/index.js';
import type { ModalProps } from '@trinity/chat-components/components/Modal/Modal.js';
import { Modal } from '@trinity/chat-components/components/Modal/Modal.js';
import Toast from '@trinity/chat-components/components/Toast/index.js';
import { useQueryDownloader } from '@trinity/chat-components/containers/QueryDownloader/index.js';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/Button.ActivityIndicator.js';
import styles from './style/ProjectReportModal.module.css';

type Props = IProjectId & Pick<ModalProps, 'open' | 'onClose'>;

type ReportType = 'xlsx';

type DropdownItem = {
  id: ReportType;
  value: string;
};

export const ExportProjectHighlightsModal = (props: Props) => {
  return (
    <ModalContents {...props} />
  );
};

export const ModalContents = ({ onClose, open, projectId }: Props) => {
  const [format, setFormat] = useState<DropdownItem | null>(null);

  const apiClient = useChatApiClient();

  const { isFetching, download } = useQueryDownloader({
    queryKey: [`get:projects/tagged-moments/export`, projectId],
    queryFn: () => apiClient.projects.exportProjectTaggedMoments({ projectId }),
    downloaderOptions: {
      onResponse: res => ({
        type: 'websocket',
        value: res.data.websocketKey,
      }),
    },
    onSuccess: () => onClose(),
    onError: () => {
      Toast.error({
        body: 'Project Report',
        title: 'There was an error trying to download the report.',
      });
    },
  });

  const handleSubmit = useCallback(() => {
    download({
      extension: format.id,
      name: `Project Highlights`,
      title: `Generating Report`,
    });
  }, [
    download,
    format,
  ]);

  const dropdownItems: DropdownItem[] = useMemo(() => {
    return exportFormats
      .map(m => ({
        id: m,
        value: m.toLocaleUpperCase(),
      }));
  }, []);

  const handleSelect = useCallback((value: SingleValue<DropdownItem>) => {
    if (value) {
      setFormat(value);
    }
  }, [setFormat]);

  return (
    <Modal
      className={styles.root}
      open={open}
      onClose={onClose}>
      <Header text="Project Report" />
      <div className={styles.dropdown}>
        <Select<DropdownItem>
          placeholder="Choose Format"
          options={dropdownItems}
          value={format}
          onChange={handleSelect}
          getOptionLabel={(item: DropdownItem) => item.value.toLocaleUpperCase()}
          getOptionValue={(item: DropdownItem) => item.id} />
      </div>
      <div className={styles.btns}>
        <ButtonActivityIndicator
          className={styles.btn}
          disabled={!format}
          onClick={handleSubmit}
          loading={isFetching}>
          Download
        </ButtonActivityIndicator>
      </div>
    </Modal>
  );
};

const exportFormats: ReportType[] = ['xlsx'];

export const useExportProjectHighlightsModal = () => useModal(ExportProjectHighlightsModal);

export default ExportProjectHighlightsModal;